import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, CheckCircle, Info } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { PrimaryButton } from "@/components/PrimaryButton";
import {
  calcCostPerCard,
  calcGlobalCostPerCard,
  useApp,
} from "@/context/AppContext";
import { ManagerOnly } from "@/components/ManagerOnly";

export const Route = createFileRoute("/tools/costbasis-backfill")({
  component: () => (
    <ManagerOnly>
      <CostBasisBackfillPage />
    </ManagerOnly>
  ),
});

type Mode = "portfolio" | "per-lot";

interface PerLotResult {
  total: number;
  byLot: number;
  byFallback: number;
}

function CostBasisBackfillPage() {
  const {
    lots,
    cards,
    products,
    bulkUpdateCards,
    updateProduct,
    cardsLoaded,
    cardsLoading,
    loadCards,
  } = useApp();

  // The backfill iterates every card in the store. After TNK-155 phase D
  // the cards set lazy-loads; explicitly request it here so a deep-link
  // straight to this tool doesn't depend on the post-hydration auto-trigger.
  useEffect(() => {
    if (!cardsLoaded && !cardsLoading) void loadCards();
  }, [cardsLoaded, cardsLoading, loadCards]);

  const portfolioAvg = calcGlobalCostPerCard(lots);
  const zeroCostCards = useMemo(
    () => cards.filter((c) => c.costBasis === 0),
    [cards],
  );
  const affectedCount = zeroCostCards.length;

  const lotMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const lot of lots) {
      const cpc = calcCostPerCard(lot);
      if (cpc > 0) m.set(lot.id, cpc);
    }
    return m;
  }, [lots]);

  const perLotBreakdown = useMemo(() => {
    const byLotId = new Map<
      string,
      { seller: string; date: string; cpc: number; count: number }
    >();
    let fallbackCount = 0;
    for (const card of zeroCostCards) {
      const lotCpc = card.sourceLotId
        ? lotMap.get(card.sourceLotId)
        : undefined;
      if (lotCpc !== undefined && card.sourceLotId) {
        const lot = lots.find((l) => l.id === card.sourceLotId);
        const existing = byLotId.get(card.sourceLotId);
        if (existing) existing.count++;
        else
          byLotId.set(card.sourceLotId, {
            seller: lot?.seller ?? "Unknown",
            date: lot?.purchaseDate ?? "",
            cpc: lotCpc,
            count: 1,
          });
      } else {
        fallbackCount++;
      }
    }
    return { rows: Array.from(byLotId.values()), fallbackCount };
  }, [zeroCostCards, lotMap, lots]);

  const zeroCostProducts = useMemo(
    () => products.filter((p) => p.costBasis === 0),
    [products],
  );
  const affectedProductCount = zeroCostProducts.length;

  const productPerLotBreakdown = useMemo(() => {
    const byLotId = new Map<
      string,
      {
        seller: string;
        date: string;
        cpc: number;
        count: number;
        cardCount: number;
      }
    >();
    let fallbackCount = 0;
    for (const product of zeroCostProducts) {
      const lotCpc = product.sourceLotId
        ? lotMap.get(product.sourceLotId)
        : undefined;
      if (lotCpc !== undefined && product.sourceLotId) {
        const lot = lots.find((l) => l.id === product.sourceLotId);
        const existing = byLotId.get(product.sourceLotId);
        if (existing) {
          existing.count++;
          existing.cardCount += product.cardCount;
        } else {
          byLotId.set(product.sourceLotId, {
            seller: lot?.seller ?? "Unknown",
            date: lot?.purchaseDate ?? "",
            cpc: lotCpc,
            count: 1,
            cardCount: product.cardCount,
          });
        }
      } else {
        fallbackCount++;
      }
    }
    return { rows: Array.from(byLotId.values()), fallbackCount };
  }, [zeroCostProducts, lotMap, lots]);

  const [mode, setMode] = useState<Mode>("portfolio");
  const [updatedCount, setUpdatedCount] = useState<number | null>(null);
  const [perLotResult, setPerLotResult] = useState<PerLotResult | null>(null);
  const [applyingCards, setApplyingCards] = useState(false);

  const [productMode, setProductMode] = useState<Mode>("portfolio");
  const [productUpdatedCount, setProductUpdatedCount] = useState<number | null>(
    null,
  );
  const [productPerLotResult, setProductPerLotResult] =
    useState<PerLotResult | null>(null);
  const [applyingProducts, setApplyingProducts] = useState(false);

  const canApply =
    cardsLoaded &&
    portfolioAvg > 0 &&
    affectedCount > 0 &&
    updatedCount === null &&
    perLotResult === null;
  const canApplyProducts =
    portfolioAvg > 0 &&
    affectedProductCount > 0 &&
    productUpdatedCount === null &&
    productPerLotResult === null;

  const alreadyDone =
    portfolioAvg > 0 &&
    affectedCount === 0 &&
    updatedCount === null &&
    perLotResult === null;
  const productsAlreadyDone =
    portfolioAvg > 0 &&
    affectedProductCount === 0 &&
    productUpdatedCount === null &&
    productPerLotResult === null;

  const applyCards = () => {
    if (!canApply) return;
    setApplyingCards(true);
    if (mode === "portfolio") {
      const patches = zeroCostCards.map((c) => ({
        id: c.id,
        patch: { costBasis: portfolioAvg },
      }));
      bulkUpdateCards(patches);
      setUpdatedCount(zeroCostCards.length);
    } else {
      let byLot = 0;
      let byFallback = 0;
      const patches = zeroCostCards.map((c) => {
        const lotCost = c.sourceLotId ? lotMap.get(c.sourceLotId) : undefined;
        if (lotCost !== undefined) {
          byLot++;
          return { id: c.id, patch: { costBasis: lotCost } };
        }
        byFallback++;
        return { id: c.id, patch: { costBasis: portfolioAvg } };
      });
      bulkUpdateCards(patches);
      setPerLotResult({ total: zeroCostCards.length, byLot, byFallback });
    }
    setApplyingCards(false);
  };

  const applyProducts = () => {
    if (!canApplyProducts) return;
    setApplyingProducts(true);
    if (productMode === "portfolio") {
      for (const p of zeroCostProducts) {
        updateProduct({ ...p, costBasis: portfolioAvg * p.cardCount });
      }
      setProductUpdatedCount(zeroCostProducts.length);
    } else {
      let byLot = 0;
      let byFallback = 0;
      for (const p of zeroCostProducts) {
        const lotCpc = p.sourceLotId ? lotMap.get(p.sourceLotId) : undefined;
        if (lotCpc !== undefined) {
          updateProduct({ ...p, costBasis: lotCpc * p.cardCount });
          byLot++;
        } else {
          updateProduct({ ...p, costBasis: portfolioAvg * p.cardCount });
          byFallback++;
        }
      }
      setProductPerLotResult({
        total: zeroCostProducts.length,
        byLot,
        byFallback,
      });
    }
    setApplyingProducts(false);
  };

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">
          Cost Basis Backfill
        </h1>
        <p className="text-sm text-muted-foreground">
          Apply a cost per card to cards and products that currently show $0.00.
          Non-destructive — only zero-cost items are touched.
        </p>
      </div>

      <div
        className="flex-1 space-y-4 overflow-y-auto py-4 pr-1"
        style={{ scrollbarGutter: "stable" }}
      >
        {portfolioAvg <= 0 && (
          <Banner kind="warn">
            <AlertCircle className="h-4 w-4 shrink-0" />
            No lots with an estimated card count found. Add a purchase lot with
            an estimated card count to calculate the portfolio average.
          </Banner>
        )}

        {!cardsLoaded && (
          <Banner kind="info">
            <Info className="h-4 w-4 shrink-0" />
            Loading inventory — counts will update once it's ready.
          </Banner>
        )}

        {/* ── Cards section ── */}
        <SectionLabel>Cards</SectionLabel>

        <ModeToggle
          mode={mode}
          onChange={(m) => {
            setMode(m);
            setUpdatedCount(null);
            setPerLotResult(null);
          }}
        />

        <StatsCard
          title={mode === "portfolio" ? "Summary" : "Per-Lot Breakdown"}
        >
          {mode === "portfolio" ? (
            <>
              <StatRow
                label="Portfolio average cost"
                value={portfolioAvg > 0 ? `$${portfolioAvg.toFixed(4)}` : "—"}
                valueClass="text-primary"
              />
              <Divider />
              <StatRow
                label="Cards with no cost basis"
                value={String(affectedCount)}
                valueClass={
                  affectedCount > 0 ? "text-secondary" : "text-muted-foreground"
                }
              />
            </>
          ) : perLotBreakdown.rows.length === 0 &&
            perLotBreakdown.fallbackCount === 0 ? (
            <div className="text-sm text-muted-foreground">
              No zero-cost cards found.
            </div>
          ) : (
            <>
              {perLotBreakdown.rows.map((row, i) => (
                <div key={i}>
                  {i > 0 && <Divider />}
                  <LotRow
                    seller={row.seller}
                    date={row.date}
                    valueText={`$${row.cpc.toFixed(4)}`}
                    subText={`${row.count} card${row.count !== 1 ? "s" : ""}`}
                  />
                </div>
              ))}
              {perLotBreakdown.fallbackCount > 0 && (
                <>
                  {perLotBreakdown.rows.length > 0 && <Divider />}
                  <LotRow
                    seller="No usable lot average"
                    date="Fallback: portfolio avg"
                    valueText={
                      portfolioAvg > 0 ? `$${portfolioAvg.toFixed(4)}` : "—"
                    }
                    subText={`${perLotBreakdown.fallbackCount} card${perLotBreakdown.fallbackCount !== 1 ? "s" : ""}`}
                    secondary
                  />
                </>
              )}
              <Divider />
              <StatRow
                label="Total affected"
                value={String(affectedCount)}
                valueClass={
                  affectedCount > 0 ? "text-secondary" : "text-muted-foreground"
                }
              />
            </>
          )}
        </StatsCard>

        {alreadyDone && (
          <Banner kind="info">
            <CheckCircle className="h-4 w-4 shrink-0" />
            All cards already have a cost basis set. Nothing to update.
          </Banner>
        )}

        {canApply && mode === "portfolio" && (
          <Banner kind="muted">
            <Info className="h-4 w-4 shrink-0" />
            {affectedCount} card{affectedCount !== 1 ? "s" : ""} will each be
            assigned a cost basis of ${portfolioAvg.toFixed(4)}.
          </Banner>
        )}

        {canApply && mode === "per-lot" && (
          <Banner kind="muted">
            <Info className="h-4 w-4 shrink-0" />
            {perLotBreakdown.rows.length > 0 &&
              `${affectedCount - perLotBreakdown.fallbackCount} card${affectedCount - perLotBreakdown.fallbackCount !== 1 ? "s" : ""} will use their lot's average`}
            {perLotBreakdown.rows.length > 0 &&
              perLotBreakdown.fallbackCount > 0 &&
              "; "}
            {perLotBreakdown.fallbackCount > 0 &&
              `${perLotBreakdown.fallbackCount} card${perLotBreakdown.fallbackCount !== 1 ? "s" : ""} will fall back to portfolio average ($${portfolioAvg.toFixed(4)})`}
            .
          </Banner>
        )}

        <PrimaryButton
          label={`Apply to ${affectedCount} Card${affectedCount !== 1 ? "s" : ""}`}
          onClick={applyCards}
          loading={applyingCards}
          disabled={!canApply}
          className="w-full"
        />

        {updatedCount !== null && (
          <Banner kind="success">
            <CheckCircle className="h-4 w-4 shrink-0" />
            {updatedCount} card{updatedCount !== 1 ? "s" : ""} updated to $
            {portfolioAvg.toFixed(4)} each.
          </Banner>
        )}

        {perLotResult !== null && (
          <Banner kind="success">
            <CheckCircle className="h-4 w-4 shrink-0" />
            {perLotResult.total} card{perLotResult.total !== 1 ? "s" : ""}{" "}
            updated
            {perLotResult.byLot > 0 &&
              ` — ${perLotResult.byLot} using lot averages`}
            {perLotResult.byFallback > 0 &&
              `${perLotResult.byLot > 0 ? ", " : " — "}${perLotResult.byFallback} using portfolio average`}
            .
          </Banner>
        )}

        {/* ── Products section ── */}
        <div className="border-t border-border pt-4" />
        <SectionLabel>Products</SectionLabel>
        <p className="text-sm text-muted-foreground">
          Apply a cost basis to products with a $0.00 cost basis. The cost is
          calculated from the product's card count and the selected per-card
          rate.
        </p>

        <ModeToggle
          mode={productMode}
          onChange={(m) => {
            setProductMode(m);
            setProductUpdatedCount(null);
            setProductPerLotResult(null);
          }}
        />

        <StatsCard
          title={productMode === "portfolio" ? "Summary" : "Per-Lot Breakdown"}
        >
          {productMode === "portfolio" ? (
            <>
              <StatRow
                label="Portfolio average cost"
                value={
                  portfolioAvg > 0 ? `$${portfolioAvg.toFixed(4)} / card` : "—"
                }
                valueClass="text-primary"
              />
              <Divider />
              <StatRow
                label="Products with no cost basis"
                value={String(affectedProductCount)}
                valueClass={
                  affectedProductCount > 0
                    ? "text-secondary"
                    : "text-muted-foreground"
                }
              />
            </>
          ) : productPerLotBreakdown.rows.length === 0 &&
            productPerLotBreakdown.fallbackCount === 0 ? (
            <div className="text-sm text-muted-foreground">
              No zero-cost products found.
            </div>
          ) : (
            <>
              {productPerLotBreakdown.rows.map((row, i) => (
                <div key={i}>
                  {i > 0 && <Divider />}
                  <LotRow
                    seller={row.seller}
                    date={row.date}
                    valueText={`$${row.cpc.toFixed(4)} / card`}
                    subText={`${row.count} product${row.count !== 1 ? "s" : ""} · ${row.cardCount} cards`}
                  />
                </div>
              ))}
              {productPerLotBreakdown.fallbackCount > 0 && (
                <>
                  {productPerLotBreakdown.rows.length > 0 && <Divider />}
                  <LotRow
                    seller="No usable lot average"
                    date="Fallback: portfolio avg"
                    valueText={
                      portfolioAvg > 0
                        ? `$${portfolioAvg.toFixed(4)} / card`
                        : "—"
                    }
                    subText={`${productPerLotBreakdown.fallbackCount} product${productPerLotBreakdown.fallbackCount !== 1 ? "s" : ""}`}
                    secondary
                  />
                </>
              )}
              <Divider />
              <StatRow
                label="Total affected"
                value={String(affectedProductCount)}
                valueClass={
                  affectedProductCount > 0
                    ? "text-secondary"
                    : "text-muted-foreground"
                }
              />
            </>
          )}
        </StatsCard>

        {productsAlreadyDone && (
          <Banner kind="info">
            <CheckCircle className="h-4 w-4 shrink-0" />
            All products already have a cost basis set. Nothing to update.
          </Banner>
        )}

        {canApplyProducts && productMode === "portfolio" && (
          <Banner kind="muted">
            <Info className="h-4 w-4 shrink-0" />
            {affectedProductCount} product
            {affectedProductCount !== 1 ? "s" : ""} will each be assigned a cost
            basis of ${portfolioAvg.toFixed(4)} × their card count.
          </Banner>
        )}

        {canApplyProducts && productMode === "per-lot" && (
          <Banner kind="muted">
            <Info className="h-4 w-4 shrink-0" />
            {productPerLotBreakdown.rows.length > 0 &&
              `${affectedProductCount - productPerLotBreakdown.fallbackCount} product${affectedProductCount - productPerLotBreakdown.fallbackCount !== 1 ? "s" : ""} will use their lot's average`}
            {productPerLotBreakdown.rows.length > 0 &&
              productPerLotBreakdown.fallbackCount > 0 &&
              "; "}
            {productPerLotBreakdown.fallbackCount > 0 &&
              `${productPerLotBreakdown.fallbackCount} product${productPerLotBreakdown.fallbackCount !== 1 ? "s" : ""} will fall back to portfolio average ($${portfolioAvg.toFixed(4)} / card)`}
            .
          </Banner>
        )}

        <PrimaryButton
          label={`Apply to ${affectedProductCount} Product${affectedProductCount !== 1 ? "s" : ""}`}
          onClick={applyProducts}
          loading={applyingProducts}
          disabled={!canApplyProducts}
          className="w-full"
        />

        {productUpdatedCount !== null && (
          <Banner kind="success">
            <CheckCircle className="h-4 w-4 shrink-0" />
            {productUpdatedCount} product
            {productUpdatedCount !== 1 ? "s" : ""} updated using $
            {portfolioAvg.toFixed(4)} per card.
          </Banner>
        )}

        {productPerLotResult !== null && (
          <Banner kind="success">
            <CheckCircle className="h-4 w-4 shrink-0" />
            {productPerLotResult.total} product
            {productPerLotResult.total !== 1 ? "s" : ""} updated
            {productPerLotResult.byLot > 0 &&
              ` — ${productPerLotResult.byLot} using lot averages`}
            {productPerLotResult.byFallback > 0 &&
              `${productPerLotResult.byLot > 0 ? ", " : " — "}${productPerLotResult.byFallback} using portfolio average`}
            .
          </Banner>
        )}
      </div>
    </section>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

function StatsCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3 rounded-md border border-border bg-card p-4">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function StatRow({
  label,
  value,
  valueClass = "text-foreground",
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="text-sm text-foreground">{label}</div>
      <div className={`text-base font-bold ${valueClass}`}>{value}</div>
    </div>
  );
}

function LotRow({
  seller,
  date,
  valueText,
  subText,
  secondary,
}: {
  seller: string;
  date: string;
  valueText: string;
  subText: string;
  secondary?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-foreground">{seller}</div>
        {date && (
          <div className="text-[11px] text-muted-foreground">{date}</div>
        )}
      </div>
      <div className="flex flex-col items-end">
        <div
          className={`text-base font-bold ${
            secondary ? "text-secondary" : "text-primary"
          }`}
        >
          {valueText}
        </div>
        <div className="text-[11px] text-muted-foreground">{subText}</div>
      </div>
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-border" />;
}

function ModeToggle({
  mode,
  onChange,
}: {
  mode: Mode;
  onChange: (m: Mode) => void;
}) {
  return (
    <div className="flex gap-1 rounded-md bg-muted p-1">
      {(["portfolio", "per-lot"] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={`flex-1 rounded-md py-1.5 text-center text-xs font-semibold ${
            mode === m
              ? "bg-card text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {m === "portfolio" ? "Portfolio Average" : "Per-Lot Average"}
        </button>
      ))}
    </div>
  );
}

function Banner({
  kind,
  children,
}: {
  kind: "warn" | "info" | "muted" | "success";
  children: React.ReactNode;
}) {
  const classMap = {
    warn: "bg-amber-500/10 border-amber-500/40 text-amber-600",
    info: "bg-primary/10 border-primary/40 text-primary",
    muted: "bg-muted border-border text-muted-foreground",
    success: "bg-emerald-500/10 border-emerald-500/40 text-emerald-600",
  } as const;
  return (
    <div
      className={`flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm ${classMap[kind]}`}
    >
      {children}
    </div>
  );
}
